<template>
  <div class="stripe-card-fields">
    <div class="field settings">
      <label>Card Number*</label>
      <div ref="cardNumberMount" class="payment-input-wrap"></div>
    </div>
    <div class="two fields">
      <div class="field settings">
        <label>Expiry Date*</label>
        <div ref="cardExpiryMount" class="payment-input-wrap"></div>
      </div>
      <div class="field settings">
        <label>CVC*</label>
        <div ref="cardCvcMount" class="payment-input-wrap"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { loadStripeJs } from '../../helpers/loadStripe'

export default {
  name: 'StripeCardFields',
  data: function () {
    return {
      stripe: null,
      cardNumber: null,
      cardExpiry: null,
      cardCvc: null,
      mountScheduled: false,
      overlayWatcher: null
    }
  },
  mounted: function () {
    this.scheduleMount()
  },
  beforeUnmount: function () {
    this.teardownWatcher()
    this.destroyElements()
  },
  methods: {
    stripeElementStyle: function () {
      return {
        base: {
          fontSize: '16px',
          color: '#323C47',
          fontFamily: 'ProximaNova-Regular, sans-serif',
          '::placeholder': {
            color: '#707070'
          }
        },
        invalid: {
          color: '#FF4C4C'
        }
      }
    },
    teardownWatcher: function () {
      if (this.overlayWatcher) {
        this.overlayWatcher()
        this.overlayWatcher = null
      }
    },
    destroyElements: function () {
      if (this.cardNumber) {
        try { this.cardNumber.destroy() } catch (e) { /* ignore */ }
        this.cardNumber = null
      }
      if (this.cardExpiry) {
        try { this.cardExpiry.destroy() } catch (e) { /* ignore */ }
        this.cardExpiry = null
      }
      if (this.cardCvc) {
        try { this.cardCvc.destroy() } catch (e) { /* ignore */ }
        this.cardCvc = null
      }
    },
    waitUntilInteractive: function () {
      const self = this
      return new Promise(function (resolve) {
        if (!self.$store.getters['getOverlay']) {
          resolve()
          return
        }
        self.teardownWatcher()
        self.overlayWatcher = self.$watch(
          function () { return self.$store.getters['getOverlay'] },
          function (visible) {
            if (!visible) {
              self.teardownWatcher()
              window.setTimeout(resolve, 100)
            }
          }
        )
        window.setTimeout(function () {
          self.teardownWatcher()
          resolve()
        }, 1200)
      })
    },
    scheduleMount: function () {
      if (this.mountScheduled) return
      this.mountScheduled = true
      const self = this
      this.waitUntilInteractive().then(function () {
        return loadStripeJs().catch(function (err) {
          if (self.errorNotification) {
            self.errorNotification('Payment form could not load. Check your connection and try again.')
          }
          console.warn('[StripeCardFields]', err && err.message ? err.message : err)
          return null
        })
      }).then(function (Stripe) {
        if (!Stripe) return
        self.$nextTick(function () {
          self.mountElements()
        })
      })
    },
    mountElements: function () {
      if (typeof window.Stripe === 'undefined') {
        if (this.errorNotification) {
          this.errorNotification('Payment form is loading. Please try again in a moment.')
        }
        return false
      }
      const pk = this.$store.getters['getStripePk']
      if (!pk) {
        if (this.errorNotification) {
          this.errorNotification('Payment is not configured. Add VUE_APP_STRIPE_KEY to .env.local at the project root.')
        }
        return false
      }
      if (!this.$refs.cardNumberMount || !this.$refs.cardExpiryMount || !this.$refs.cardCvcMount) {
        return false
      }

      this.destroyElements()
      this.stripe = window.Stripe(pk)
      const elements = this.stripe.elements()
      const style = this.stripeElementStyle()
      this.cardNumber = elements.create('cardNumber', { style: style })
      this.cardExpiry = elements.create('cardExpiry', { style: style })
      this.cardCvc = elements.create('cardCvc', { style: style })
      this.cardNumber.mount(this.$refs.cardNumberMount)
      this.cardExpiry.mount(this.$refs.cardExpiryMount)
      this.cardCvc.mount(this.$refs.cardCvcMount)
      return true
    },
    isReady: function () {
      return !!(this.stripe && this.cardNumber)
    },
    createToken: function () {
      const self = this
      if (!this.isReady()) {
        return Promise.reject(new Error('Payment form is not ready. Please wait a moment.'))
      }
      return this.stripe.createToken(this.cardNumber).then(function (result) {
        if (result.error) {
          return Promise.reject(new Error(result.error.message || 'Invalid card details'))
        }
        return result.token
      })
    }
  }
}
</script>

<style scoped>
.payment-input-wrap {
  min-height: 42px;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  padding: 0.6rem 0.75rem;
  background: var(--color-bg-surface);
  box-sizing: border-box;
  position: relative;
  z-index: 1;
}

.payment-input-wrap >>> .StripeElement {
  width: 100%;
}

.payment-input-wrap >>> iframe {
  min-height: 20px !important;
  pointer-events: auto !important;
}
</style>
